import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiFormData } from "../../utils/api";
import { useEntitlement } from "../../hooks/useEntitlement";
import type { DocTypeSpec, KycDocument } from "../../Layouts/cargo-market/exchange/api";

// ===========================================================================
// Expediente (KYC) de la flotilla: operadores, unidades y remolques.
// Es el mismo expediente de Cargo Market (sello «Verificado»); el backend vive
// en torre-de-control-api/src/modules/fleetDocuments.
// ===========================================================================

export type FleetOwnerType = "driver" | "vehicle" | "trailer";

export interface OwnerVerification {
  documents: KycDocument[];
  ok: boolean;
  missing: string[];
  pending: string[];
  expiring: { docType: string; label: string; daysLeft: number; expiresAt: string }[];
  /** Documentos obligatorios del tipo de dueño. */
  required: number;
  /** Obligatorios aprobados y vigentes. */
  verified: number;
  /** verified / required, 0–100. */
  percent: number;
}

export interface FleetDocumentsResult {
  ownerType: FleetOwnerType;
  docTypes: Record<string, DocTypeSpec>;
  owners: Record<string, OwnerVerification>;
}

export type MyDriverDocuments =
  | (OwnerVerification & { driverId: string; docTypes: Record<string, DocTypeSpec> })
  /** El usuario no está dado de alta como operador. */
  | { driverId: null };

export interface DocumentUpload {
  docType: string;
  number?: string;
  issuedAt?: string;
  expiresAt?: string;
  file?: File | null;
}

const unwrap = <T,>(res: { data: { data: T } }) => res.data.data;

function toFormData(input: DocumentUpload, extra: Record<string, string> = {}) {
  const fd = new FormData();
  for (const [k, v] of Object.entries(extra)) fd.append(k, v);
  fd.append("docType", input.docType);
  if (input.number) fd.append("number", input.number);
  if (input.issuedAt) fd.append("issuedAt", input.issuedAt);
  if (input.expiresAt) fd.append("expiresAt", input.expiresAt);
  if (input.file) fd.append("file", input.file, input.file.name);
  return fd;
}

export const fleetDocumentsApi = {
  list: async (ownerType: FleetOwnerType, ids: string[]) =>
    unwrap<FleetDocumentsResult>(await api.get("/fleetDocuments", { params: { ownerType, ids: ids.join(",") } })),
  upload: async (ownerType: FleetOwnerType, ownerId: string, input: DocumentUpload) =>
    unwrap<KycDocument>(await apiFormData.post("/fleetDocuments", toFormData(input, { ownerType, ownerId }))),
  mine: async () => unwrap<MyDriverDocuments>(await api.get("/myDriverDocuments")),
  uploadMine: async (input: DocumentUpload) => unwrap<KycDocument>(await apiFormData.post("/myDriverDocuments", toFormData(input))),
};

export const FLEET_DOCUMENTS_KEY = "fleetDocuments";

/** Expediente de varios registros (las tarjetas de una página) en una sola consulta. */
/**
 * El expediente es el KYC del Cargo Market: sólo existe donde su espacio de
 * transportista está habilitado. En CC-CONSOLE el Cargo Market nace apagado
 * (tenantDefaults), así que Administración de Flotilla no muestra el % ni los
 * documentos y no los consulta.
 */
export const FLEET_DOCUMENTS_ENTITLEMENT = "app:cargo-market-transportista";
export function useFleetDocumentsEnabled(): boolean {
  return useEntitlement(FLEET_DOCUMENTS_ENTITLEMENT);
}

export function useFleetDocuments(ownerType: FleetOwnerType, ids: string[]) {
  const on = useFleetDocumentsEnabled();
  const sorted = [...new Set(ids.filter(Boolean))].sort();
  return useQuery({
    queryKey: [FLEET_DOCUMENTS_KEY, ownerType, sorted],
    queryFn: () => fleetDocumentsApi.list(ownerType, sorted),
    enabled: on && sorted.length > 0,
    staleTime: 60_000,
  });
}

export function useMyDriverDocuments(enabled = true) {
  const on = useFleetDocumentsEnabled();
  return useQuery({
    queryKey: [FLEET_DOCUMENTS_KEY, "mine"],
    queryFn: fleetDocumentsApi.mine,
    enabled: enabled && on,
    retry: false,
    staleTime: 60_000,
  });
}

/** Tras subir un documento: refresca los % de las tarjetas, el panel y el perfil. */
export function useInvalidateFleetDocuments() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [FLEET_DOCUMENTS_KEY] });
}
